/**
 * Banner DOM rendering inside an isolated shadow root.
 *
 * Two views, swapped in-place when the visitor interacts:
 *
 *   1. SUMMARY view (default landing, unless categoriesOnFirstLayer=true)
 *      - title + body text + privacy link
 *      - three buttons of equal weight: [Personnaliser] [Refuser] [Accepter tout]
 *
 *   2. PREFERENCES view (after "Personnaliser" click, or directly if the
 *      site owner enabled categoriesOnFirstLayer)
 *      - title + "← Retour" link back to summary
 *      - scrollable list of categories with toggles
 *      - actions: [Refuser] [Accepter tout] [Enregistrer mes choix]
 *
 * The X close in the header does NOT submit a choice — it collapses the
 * banner to a small floating "reopen" icon (when enabled). The reopen
 * icon is rendered as a second top-level host element so its lifecycle
 * is independent from the banner's. It is also displayed after a real
 * submission so the visitor can revisit their choices later.
 *
 * Shadow DOM ⇒ host site CSS cannot leak in, and the banner's CSS
 * cannot leak out. Focus is moved to the primary CTA on mount; ARIA
 * roles announce the dialog to screen readers.
 */

import { resolveAssetUrl } from "./assets.js";
import inlinedCss from "./styles.css";
import { SPEED_MS } from "./theme.js";
import type { BannerSettings, ConsentChoices } from "./types.js";

const POWERED_BY_IMAGE_PATH = "/logo/consenteer_poweredBy.png";

const HOST_ID = "csteer_banner_host";
const REOPEN_HOST_ID = "csteer_reopen_host";

export interface BannerHandle {
  /** Mount the full banner (summary or preferences view). */
  mount(): void;
  /**
   * Open the banner directly on a given view, mounting it if needed and
   * hiding the floating icon. Used by the public `csteer.openPreferences()`
   * API so a "Manage my cookies" link can reopen preferences WITHOUT erasing
   * the stored choice.
   */
  open(view: "summary" | "preferences"): void;
  /**
   * Mount ONLY the floating reopen icon (no banner). Used by the
   * editor's icon preview so the visitor-side icon can be inspected
   * without the modal overlay. Clicking the icon then opens the
   * banner the normal way.
   */
  mountCollapsed(): void;
  unmount(): void;
  isMounted(): boolean;
}

export interface BannerOpts {
  /** Site identifier — used to tag the "Powered by" link with ?utm_source. */
  siteId: string;
  settings: BannerSettings;
  initialChoices: ConsentChoices;
  onSubmit: (choices: ConsentChoices) => void;
  onOpen?: () => void;
  onClose?: () => void;
  /** Fired when the visitor collapses the banner (X / Escape) WITHOUT
   *  submitting a choice — distinct from onClose, which also fires after a
   *  submit. Lets init() remember the dismissal for the session. */
  onCollapse?: () => void;
  applyThemeToHost: (host: HTMLElement) => void;
}

type View = "summary" | "preferences";

export function createBanner(opts: BannerOpts): BannerHandle {
  let hostEl: HTMLElement | null = null;
  let shadow: ShadowRoot | null = null;
  let reopenHostEl: HTMLElement | null = null;
  /** Element that had focus before the banner opened. We restore focus to
   *  it when the banner closes so keyboard users don't lose their place. */
  let previouslyFocused: HTMLElement | null = null;
  /** Pushdown-only: original body margin saved before we override it,
   *  restored on unmount. ResizeObserver keeps the margin equal to the
   *  banner's current height (changes between summary and preferences). */
  let pushdownOriginalBodyMargin: string | null = null;
  let pushdownResizeObserver: ResizeObserver | null = null;
  const currentChoices: ConsentChoices = { ...opts.initialChoices };
  let view: View = opts.settings.layout.categoriesOnFirstLayer ? "preferences" : "summary";

  function mount(): void {
    if (hostEl) return;
    // If the floating icon was visible (after a previous submission or collapse),
    // hide it while the banner is open.
    removeReopenIcon();

    previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // A banner still playing its exit animation keeps its node in the page for
    // a moment after we've dropped our reference to it. Clear any leftover so
    // reopening mid-exit can't leave two hosts sharing the same id.
    document.getElementById(HOST_ID)?.remove();

    hostEl = document.createElement("div");
    hostEl.id = HOST_ID;
    hostEl.setAttribute("data-format", positionFamily(opts.settings.layout.position));
    hostEl.setAttribute("data-anim", effectiveAnimation());
    hostEl.lang = opts.settings.language;
    opts.applyThemeToHost(hostEl);
    shadow = hostEl.attachShadow({ mode: "open" });

    const style = document.createElement("style");
    style.textContent = inlinedCss;
    shadow.appendChild(style);

    shadow.appendChild(renderRoot());

    document.body.appendChild(hostEl);
    document.addEventListener("keydown", handleKeydown, true);
    enablePushdownIfNeeded();
    focusPrimary();
    opts.onOpen?.();
  }

  /**
   * Pushdown family: the banner is sticky at the top/bottom AND the host
   * page is pushed by the same amount, so nothing is occluded. We measure
   * the live banner height (changes between summary/preferences views)
   * with a ResizeObserver and mirror it onto document.body's margin.
   */
  function enablePushdownIfNeeded(): void {
    if (!hostEl) return;
    const family = positionFamily(opts.settings.layout.position);
    if (family !== "pushdown") return;

    const isTop = opts.settings.layout.position === "pushdown-top";
    const sideProp = isTop ? "marginTop" : "marginBottom";

    // Save the host page's current margin so we can restore it on unmount.
    pushdownOriginalBodyMargin = document.body.style[sideProp] || "";

    const apply = () => {
      if (!hostEl) return;
      const height = hostEl.getBoundingClientRect().height;
      document.body.style[sideProp] = `${Math.round(height)}px`;
    };
    apply();
    pushdownResizeObserver = new ResizeObserver(apply);
    pushdownResizeObserver.observe(hostEl);
  }

  function disablePushdown(): void {
    if (pushdownResizeObserver) {
      pushdownResizeObserver.disconnect();
      pushdownResizeObserver = null;
    }
    if (pushdownOriginalBodyMargin !== null) {
      const isTop = opts.settings.layout.position === "pushdown-top";
      const sideProp = isTop ? "marginTop" : "marginBottom";
      document.body.style[sideProp] = pushdownOriginalBodyMargin;
      pushdownOriginalBodyMargin = null;
    }
  }

  /** Hard-unmount: removes both the banner and the floating icon. */
  function unmount(): void {
    removeBanner();
    removeReopenIcon();
  }

  /** Soft collapse: removes the banner but leaves the floating icon visible. */
  function collapseToIcon(): void {
    removeBanner();
    renderReopenIcon();
    opts.onCollapse?.();
    // When the icon is visible, hand focus to it so keyboard users can
    // reopen the banner without hunting for the trigger.
    const reopenBtn = reopenHostEl?.shadowRoot?.querySelector<HTMLButtonElement>(".csteer_reopen");
    reopenBtn?.focus();
  }

  /**
   * The effect actually in force. Mirrors buildMotionVars in theme.ts: the
   * pushdown family can't travel because it drives a body margin, so a
   * requested slide degrades to a fade there.
   */
  function effectiveAnimation(): string {
    const anim = opts.settings.layout.animation ?? "slide";
    if (anim === "slide" && opts.settings.layout.position.startsWith("pushdown-")) return "fade";
    return anim;
  }

  function removeBanner(): void {
    if (!hostEl) return;
    const leaving = hostEl;
    const leavingShadow = shadow;
    document.removeEventListener("keydown", handleKeydown, true);
    disablePushdown();
    // Drop our references BEFORE the node leaves the DOM: isMounted() has to
    // report false immediately, otherwise a mount() during the exit would
    // stack a second banner on top of the one on its way out.
    hostEl = null;
    shadow = null;

    const banner = leavingShadow?.querySelector<HTMLElement>(".csteer_banner");
    if (banner && effectiveAnimation() !== "none") {
      leaving.setAttribute("data-anim-state", "closing");
      let removed = false;
      const drop = () => {
        if (removed) return;
        removed = true;
        leaving.remove();
      };
      banner.addEventListener("animationend", drop, { once: true });
      // Fallback: prefers-reduced-motion collapses the animation to 0.001ms and
      // a hidden host may never fire animationend at all. Never leave the node
      // stranded in the page because an event didn't arrive.
      const ms = SPEED_MS[opts.settings.layout.animationSpeed ?? "normal"] ?? 200;
      setTimeout(drop, ms + 120);
    } else {
      leaving.remove();
    }

    // Everything below stays synchronous: the banner is logically closed the
    // moment it is asked to go, only its node lingers for the exit. Focus must
    // not stay trapped inside an element that is fading out.
    // Restore focus unless the caller has already moved focus elsewhere
    // (e.g. collapseToIcon focuses the reopen button after this runs).
    queueMicrotask(() => {
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
      previouslyFocused = null;
    });
    opts.onClose?.();
  }

  /**
   * Keyboard handler attached at the document level (capture phase) so
   * presses inside the shadow root are observed.
   * - Escape ⇒ collapse to icon (RGAA 7.3 / 13.x)
   * - Tab ⇒ for modal formats (popup, sidebar), confine focus to the dialog
   */
  function handleKeydown(e: KeyboardEvent): void {
    if (!shadow || !hostEl) return;
    if (e.key === "Escape") {
      e.stopPropagation();
      e.preventDefault();
      collapseToIcon();
      return;
    }
    if (e.key !== "Tab") return;
    if (!isModalFormat()) return;
    trapTab(e);
  }

  function isModalFormat(): boolean {
    const family = positionFamily(opts.settings.layout.position);
    return family === "popup" || family === "sidebar";
  }

  function trapTab(e: KeyboardEvent): void {
    if (!shadow) return;
    const focusable = collectFocusable(shadow);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    const active = shadow.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !active) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function rerender(): void {
    if (!shadow) return;
    const old = shadow.querySelector(".csteer_banner");
    old?.remove();
    shadow.appendChild(renderRoot());
    focusPrimary();
  }

  /**
   * Open the banner on `targetView`. If already mounted (e.g. summary is
   * showing), switch the view in place; otherwise mount fresh. mount() hides
   * the floating icon. The stored choice is left untouched.
   */
  function open(targetView: View): void {
    view = targetView;
    if (hostEl) rerender();
    else mount();
  }

  function focusPrimary(): void {
    // Move focus to the dialog container (not the accept button) so assistive
    // tech announces the dialog WITHOUT painting a focus ring on the primary
    // button the moment the banner opens. Keyboard users then Tab to the
    // buttons, which show their own :focus-visible ring.
    const dialog = shadow?.querySelector<HTMLElement>(".csteer_banner");
    dialog?.focus();
  }

  function collectFocusable(root: ShadowRoot): HTMLElement[] {
    const selector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => !el.hasAttribute("aria-hidden"),
    );
  }

  // ===========================================================================
  // Render — top-level
  // ===========================================================================

  function renderRoot(): HTMLElement {
    const root = document.createElement("div");
    root.className = "csteer_banner";
    root.setAttribute("data-view", view);
    // RGAA 7.x — declare the dialog role + modal status truthfully. Only
    // formats that visually trap attention (popup, sidebar) are flagged
    // as aria-modal=true, which also turns on the Tab trap above.
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", isModalFormat() ? "true" : "false");
    root.setAttribute("aria-labelledby", "csteer_title");
    // Focusable so we can move focus here on open (see focusPrimary) — the
    // container itself never shows a focus ring (styles.css).
    root.setAttribute("tabindex", "-1");
    if (view === "summary") root.setAttribute("aria-describedby", "csteer_body");

    if (view === "summary") {
      root.appendChild(renderHeader(opts.settings.texts.headerTitle, /* showBack */ false));
      root.appendChild(renderBody());
      root.appendChild(renderSummaryActions());
    } else {
      root.appendChild(renderHeader(opts.settings.texts.preferencesTitle, /* showBack */ true));
      root.appendChild(renderPrefsArea());
      root.appendChild(renderPrefsActions());
    }

    // White-label: PRO/ENTERPRISE plans hide the badge (server sets poweredBy=false).
    if (opts.settings.poweredBy !== false) root.appendChild(renderPowered());
    return root;
  }

  // ===========================================================================
  // Render — shared blocks
  // ===========================================================================

  function renderHeader(title: string, showBack: boolean): HTMLElement {
    const header = document.createElement("div");
    header.className = "csteer_header";

    // Left side: optional "back to summary" link, then title.
    const left = document.createElement("div");
    left.className = "csteer_header_left";

    if (showBack) {
      const back = document.createElement("button");
      back.className = "csteer_back_link";
      back.type = "button";
      back.textContent = opts.settings.texts.backToSummary;
      back.addEventListener("click", () => {
        view = "summary";
        rerender();
      });
      left.appendChild(back);
    }

    const titleEl = document.createElement("h2");
    titleEl.className = "csteer_title";
    titleEl.id = "csteer_title";
    titleEl.textContent = title;
    left.appendChild(titleEl);

    const close = document.createElement("button");
    close.className = "csteer_close";
    close.type = "button";
    close.setAttribute("aria-label", "Réduire");
    // The "×" glyph is purely decorative; the accessible name comes
    // from aria-label so screen readers don't announce "multiplication
    // sign". aria-hidden on the inner span keeps the visual icon.
    const closeGlyph = document.createElement("span");
    closeGlyph.setAttribute("aria-hidden", "true");
    closeGlyph.textContent = "×";
    close.appendChild(closeGlyph);
    // Closing does NOT submit a choice — it just collapses to the floating
    // icon so the visitor can come back later.
    close.addEventListener("click", () => collapseToIcon());

    header.append(left, close);
    return header;
  }

  function renderBody(): HTMLElement {
    const body = document.createElement("p");
    body.className = "csteer_body";
    body.id = "csteer_body";
    body.innerHTML = `${escapeHtmlAllowingBr(opts.settings.texts.body)} ${renderTermsLink()}`;
    return body;
  }

  function renderTermsLink(): string {
    const href = escapeAttr(opts.settings.texts.termsLinkUrl);
    const text = escapeText(opts.settings.texts.termsLinkText);
    // RGAA 13.x — announce that this link opens in a new window. The
    // visually-hidden span is read by AT after the link text.
    return ` <a class="csteer_link" href="${href}" target="_blank" rel="noopener noreferrer">${text}<span class="csteer_sr_only"> (nouvelle fenêtre)</span></a>`;
  }

  function renderPowered(): HTMLElement {
    const el = document.createElement("div");
    el.className = "csteer_powered";
    const src = escapeAttr(resolveAssetUrl(POWERED_BY_IMAGE_PATH));
    // Tag the link so back-office traffic from a banner is attributable to the
    // site that displayed it (utm_source = the displaying domain, www. stripped;
    // falls back to the site id if the hostname isn't available).
    const host = (typeof window !== "undefined" && window.location?.hostname) || opts.siteId;
    const source = host.replace(/^www\./, "");
    const href = escapeAttr(`https://consenteer.com/?utm_source=${encodeURIComponent(source)}`);
    el.innerHTML = `<a href="${href}" target="_blank" rel="noopener noreferrer" aria-label="Powered by Consenteer"><img src="${src}" alt="Powered by Consenteer" /></a>`;
    return el;
  }

  // ===========================================================================
  // Summary view — 3 equal-weight buttons
  // ===========================================================================

  function renderSummaryActions(): HTMLElement {
    const actions = document.createElement("div");
    actions.className = "csteer_actions";

    const customize = document.createElement("button");
    customize.className = "csteer_btn csteer_btn_customize";
    customize.type = "button";
    customize.textContent = opts.settings.texts.customizeButton;
    customize.addEventListener("click", () => {
      view = "preferences";
      rerender();
    });

    const refuse = document.createElement("button");
    refuse.className = "csteer_btn csteer_btn_refuse";
    refuse.type = "button";
    refuse.textContent = opts.settings.texts.refuseButton;
    refuse.addEventListener("click", () => submit(denyAll()));

    const accept = document.createElement("button");
    accept.className = "csteer_btn csteer_btn_accept";
    accept.type = "button";
    accept.textContent = opts.settings.texts.acceptButton;
    accept.addEventListener("click", () => submit(allowAll(opts.settings.categories)));

    // Order intentional: Refuser → Personnaliser → Tout accepter.
    // Putting "Refuser" first removes any visual hierarchy hinting that
    // accepting is the expected choice — required by the CNIL ("the
    // refusal action must be as easy as the acceptance action").
    actions.append(refuse, customize, accept);
    return actions;
  }

  // ===========================================================================
  // Preferences view — scrollable categories + Save
  // ===========================================================================

  function renderPrefsArea(): HTMLElement {
    const area = document.createElement("div");
    area.className = "csteer_prefs";

    // Labels + descriptions are read from settings.texts.categoryTexts so
    // the site owner can override them per-banner. Order is intentional:
    // 'necessary' first (always-on), then the four configurable rows.
    const ORDER: (keyof ConsentChoices)[] = [
      "necessary",
      "analytics",
      "personalization",
      "marketing",
      "social",
    ];
    const texts = opts.settings.texts.categoryTexts;

    for (const key of ORDER) {
      if (key !== "necessary" && opts.settings.categories?.[key] === false) continue;
      const cat = {
        key,
        label: texts[key].label,
        description: texts[key].description,
        // Derived from the declared cookie inventory (settings.cookies[key]),
        // so the pill always reflects the real list. Falls back to a manually
        // set cookieCount for sites without an inventory (back-compat).
        cookieCount: opts.settings.cookies?.[key]?.length || texts[key].cookieCount || 0,
      };

      const row = document.createElement("div");
      row.className = "csteer_pref";

      const labelWrap = document.createElement("div");
      labelWrap.className = "csteer_pref_label";
      // The cookie count pill is rendered only when > 0 — categories without
      // a declared count just show the label, keeping the row compact.
      const countPill =
        cat.cookieCount > 0
          ? ` <span class="csteer_pref_count" aria-label="${cat.cookieCount} cookies">${cat.cookieCount}</span>`
          : "";
      labelWrap.innerHTML = `<strong>${escapeText(cat.label)}${countPill}</strong><small>${escapeText(cat.description)}</small>`;

      const toggle = document.createElement("label");
      toggle.className = "csteer_toggle";

      const input = document.createElement("input");
      input.type = "checkbox";
      input.checked = currentChoices[cat.key] === true;
      input.disabled = cat.key === "necessary";
      // RGAA 11.1 / 11.2 — every form control gets an accessible name AND
      // an explicit role/state. role="switch" + aria-checked matches the
      // visual switch pattern better than the default checkbox role.
      input.setAttribute("role", "switch");
      input.setAttribute("aria-label", cat.label);
      input.setAttribute("aria-checked", String(input.checked));
      if (cat.key === "necessary") input.setAttribute("aria-disabled", "true");
      input.addEventListener("change", () => {
        if (cat.key === "necessary") return;
        (currentChoices as Record<string, boolean>)[cat.key] = input.checked;
        input.setAttribute("aria-checked", String(input.checked));
      });

      const track = document.createElement("span");
      track.className = "csteer_toggle_track";
      const thumb = document.createElement("span");
      thumb.className = "csteer_toggle_thumb";
      track.appendChild(thumb);

      toggle.append(input, track);
      row.append(labelWrap, toggle);
      area.appendChild(row);

      // Cookie detail accordion (native <details> for built-in a11y): lists the
      // declared cookies for this category with provider / duration / purpose.
      const cookies = opts.settings.cookies?.[cat.key] ?? [];
      if (cookies.length) area.appendChild(renderCookieList(cat.key, cat.description, cookies));
    }

    return area;
  }

  function renderCookieList(
    key: keyof ConsentChoices,
    categoryDescription: string,
    cookies: NonNullable<BannerSettings["cookies"]>[keyof ConsentChoices],
  ): HTMLElement {
    const lang = opts.settings.language;
    const n = cookies.length;
    const summaryText =
      lang === "en" ? `View ${n} cookie${n > 1 ? "s" : ""}` : `Voir ${n} cookie${n > 1 ? "s" : ""}`;

    const details = document.createElement("details");
    details.className = "csteer_cookies";
    details.setAttribute("data-category", key);

    const summary = document.createElement("summary");
    summary.className = "csteer_cookies_summary";
    summary.textContent = summaryText;
    details.appendChild(summary);

    const list = document.createElement("ul");
    list.className = "csteer_cookies_list";
    for (const ck of cookies) {
      const li = document.createElement("li");
      li.className = "csteer_cookie";
      // Purpose falls back to the category description when not provided.
      const purpose = ck.purpose?.trim() ? ck.purpose : categoryDescription;
      const meta = [ck.provider, ck.duration]
        .filter((v): v is string => Boolean(v))
        .map(escapeText)
        .join(" · ");
      const metaHtml = meta ? `<span class="csteer_cookie_meta">${meta}</span>` : "";
      li.innerHTML = `<span class="csteer_cookie_name">${escapeText(ck.name)}</span>${metaHtml}<span class="csteer_cookie_purpose">${escapeText(purpose)}</span>`;
      list.appendChild(li);
    }
    details.appendChild(list);
    return details;
  }

  function renderPrefsActions(): HTMLElement {
    const actions = document.createElement("div");
    actions.className = "csteer_actions csteer_actions_prefs";

    const refuse = document.createElement("button");
    refuse.className = "csteer_btn csteer_btn_refuse";
    refuse.type = "button";
    refuse.textContent = opts.settings.texts.refuseButton;
    refuse.addEventListener("click", () => submit(denyAll()));

    const accept = document.createElement("button");
    accept.className = "csteer_btn csteer_btn_accept";
    accept.type = "button";
    accept.textContent = opts.settings.texts.acceptButton;
    accept.addEventListener("click", () => submit(allowAll(opts.settings.categories)));

    const save = document.createElement("button");
    save.className = "csteer_btn csteer_btn_save";
    save.type = "button";
    save.textContent = opts.settings.texts.savePreferencesButton;
    save.addEventListener("click", () => submit({ ...currentChoices }));

    actions.append(refuse, accept, save);
    return actions;
  }

  // ===========================================================================
  // Floating reopen icon
  // ===========================================================================

  function renderReopenIcon(): void {
    if (reopenHostEl) return;
    if (!opts.settings.reopenIcon?.enabled) return;

    const host = document.createElement("div");
    host.id = REOPEN_HOST_ID;
    host.setAttribute("data-reopen-position", opts.settings.reopenIcon.position);
    host.setAttribute("data-reopen-format", opts.settings.reopenIcon.format);
    host.setAttribute("data-reopen-anim", opts.settings.reopenIcon.animation ?? "fade");
    host.lang = opts.settings.language;
    opts.applyThemeToHost(host);

    const root = host.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = inlinedCss;
    root.appendChild(style);

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "csteer_reopen";
    btn.setAttribute("aria-label", opts.settings.reopenIcon.ariaLabel);

    // Image is the only visual — accessible name comes from aria-label,
    // alt="" tells AT the image is decorative.
    const img = document.createElement("img");
    img.className = "csteer_reopen_img";
    img.alt = "";
    img.src = resolveAssetUrl(opts.settings.reopenIcon.imageUrl);
    btn.appendChild(img);

    btn.addEventListener("click", () => {
      removeReopenIcon();
      mount();
    });
    root.appendChild(btn);

    document.body.appendChild(host);
    reopenHostEl = host;
  }

  function removeReopenIcon(): void {
    if (!reopenHostEl) return;
    reopenHostEl.remove();
    reopenHostEl = null;
  }

  // ===========================================================================

  function submit(choices: ConsentChoices): void {
    // Keep the banner's own state in sync so a later re-open (via the icon
    // or csteer.openPreferences()) shows toggles reflecting this choice.
    Object.assign(currentChoices, choices);
    opts.onSubmit(choices);
    removeBanner();
    // After a real submission we keep the floating icon visible so the
    // visitor can revisit their choices later.
    renderReopenIcon();
  }

  return {
    mount,
    open,
    mountCollapsed: () => renderReopenIcon(),
    unmount,
    isMounted: () => hostEl != null,
  };
}

// =============================================================================
// helpers
// =============================================================================

function positionFamily(position: BannerSettings["layout"]["position"]): string {
  if (position === "bottom-right" || position === "bottom-left" || position === "bottom-center") {
    return "box";
  }
  if (
    position === "banner-bottom" ||
    position === "banner-top" ||
    position === "bottom-banner" ||
    position === "top-banner"
  ) {
    return "banner";
  }
  if (position === "popup-center" || position === "center-modal") return "popup";
  if (position === "sidebar-left" || position === "sidebar-right") return "sidebar";
  if (position === "pushdown-top" || position === "pushdown-bottom") return "pushdown";
  return "box";
}

/**
 * Grant everything the site actually OFFERS. Categories disabled in the
 * site's config are never shown in the preferences view, so "accept all"
 * must not grant them either — consent has to stay specific and informed.
 */
function allowAll(categories?: BannerSettings["categories"]): ConsentChoices {
  return {
    necessary: true,
    analytics: categories?.analytics !== false,
    marketing: categories?.marketing !== false,
    personalization: categories?.personalization !== false,
    social: categories?.social !== false,
  };
}

function denyAll(): ConsentChoices {
  return {
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
    social: false,
  };
}

function escapeText(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}

function escapeAttr(s: string): string {
  return escapeText(s);
}

/** Allow <br/> only; everything else is escaped. */
function escapeHtmlAllowingBr(s: string): string {
  return escapeText(s).replace(/&lt;br\s*\/?&gt;/gi, "<br/>");
}
